import deburr from "lodash/deburr";

/**
 * Helpers de manipulation de chaînes remplaçant le package `string` (~35 KB
 * source, chargé en eager dans `app-*.js`).
 *
 * Les méthodes implémentées ici reproduisent strictement le comportement de
 * `S(s).latinise().s`, `S(s).stripPunctuation().s` et `S(s).slugify().s` tels
 * qu'utilisés dans le code legacy Backbone (tests de non-régression via les
 * usages existants).
 *
 * `lodash/deburr` est déjà embarqué dans le chunk eager (via d'autres
 * imports), il remplace donc `latinise` sans coût ajouté.
 */

/** Transliteration Unicode → ASCII (ex: "éà" → "ea"). Équivaut à `S(s).latinise().s`. */
export const latinise = (s: string): string => deburr(s);

/**
 * Supprime la ponctuation et compacte les espaces consécutifs.
 * Équivalent strict de `S(s).stripPunctuation().s` :
 *   `s.replace(/[^\w\s]|_/g, "").replace(/\s+/g, " ")`.
 */
export const stripPunctuation = (s: string): string =>
  s.replace(/[^\w\s]|_/g, "").replace(/\s+/g, " ");

/**
 * Reproduit `S(s).slugify().s` :
 *   latinise → remove non-[\w\s-] → lowercase → dasherize → trim leading dash.
 * La lib `string` fait `new S(...).latinise().s.replace(/[^\w\s-]/g, '').toLowerCase()`
 * puis `dasherize()` (trim + [_\s]+ → "-" + camelCase split) puis drop leading "-".
 */
export const slugify = (input: string): string => {
  const deburred = latinise(input).replace(/[^\w\s-]/g, "").toLowerCase();
  const dasherized = deburred
    .trim()
    .replace(/[_\s]+/g, "-")
    .replace(/([A-Z])/g, "-$1")
    .replace(/-+/g, "-")
    .toLowerCase();
  return dasherized.charAt(0) === "-" ? dasherized.slice(1) : dasherized;
};
